import { useEffect, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Grid,
  Step,
  StepLabel,
  Stepper,
  useTheme,
} from "@mui/material";

import { trackmateData } from "@/utils/staticData/trackmateData";
import useResponsive from "@/hooks/useResponsive";

import {
  StepperIcon,
  StepperMarks,
  StepperContentCard,
  Heading,
} from "./components";
import {
  getStepperStyles,
  imageContainerStyles,
  imageWrapperStyles,
  stepperInnerContainerStyles,
  stepperOuterContainerStyles,
} from "./styles";
import AnimatedImage from "../UI-Kit/AnimatedImage";

const GetTrackmateStepper = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const stepperRef = useRef<HTMLElement>();
  const containerRef = useRef<HTMLElement>();

  const isMobile = useResponsive("down", "md");
  const theme = useTheme();

  // Handling scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (stepperRef.current && containerRef.current) {
        const value =
          stepperRef.current.offsetTop - containerRef.current.offsetTop;

        if (value >= 0 && value < 1200) {
          const step = Math.floor(value / 300);
          setActiveStep(step);
        }

        // Scroll to the active step
        let stepperMark: HTMLElement | null = null;

        if (value >= 0 && value < 300) {
          stepperMark = document.getElementById("stepper-start");
        } else if (value >= 300 && value < 600) {
          stepperMark = document.getElementById("step-0");
        } else if (value >= 600 && value < 900) {
          stepperMark = document.getElementById("step-3");
        } else if (value >= 900 && value < 1200) {
          stepperMark = document.getElementById("stepper-end");
        }

        if (stepperMark) {
          stepperMark.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup function to remove scroll event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <Heading />
      <Box
        sx={{ height: "2000px", pt: { xs: 5, md: 0 } }}
        mx="auto"
        ref={containerRef}
      >
        <Box ref={stepperRef} sx={stepperOuterContainerStyles}>
          <Grid container spacing={0}>
            {/* Left side desktop - image */}
            {!isMobile && (
              <Grid item xs={12} md={4.5} lg={5} sx={imageContainerStyles}>
                <Box display="flex" flexDirection="column" width="100%">
                  {trackmateData.map(
                    ({ title, imageUrl, thumbnail }, index) => (
                      <Collapse in={activeStep === index} key={index}>
                        <AnimatedImage
                          sx={imageWrapperStyles}
                          thumbnailUrl={thumbnail}
                          mainUrl={imageUrl}
                          alt={title}
                        />
                      </Collapse>
                    )
                  )}
                </Box>
              </Grid>
            )}

            {/* Central part - stepper */}
            <Grid item xs={12} md={3} lg={2}>
              <Box sx={stepperInnerContainerStyles}>
                <StepperMarks
                  title="Beginning"
                  id="stepper-start"
                  mr={{ xs: 4, md: "auto" }}
                  mb={{ md: 4 }}
                />
                <Stepper
                  activeStep={activeStep}
                  orientation={isMobile ? "horizontal" : "vertical"}
                  sx={getStepperStyles(theme)}
                >
                  {trackmateData.map((_, index) => (
                    <Step key={index} id={`step-${index}`}>
                      <StepLabel
                        icon={
                          <StepperIcon index={index} activeStep={activeStep} />
                        }
                      />
                    </Step>
                  ))}
                </Stepper>
                <StepperMarks
                  title="Enjoy being unique"
                  whiteSpace="nowrap"
                  id="stepper-end"
                />
              </Box>
            </Grid>

            {/* Right side desktop and mobile content card */}
            <Grid item xs={12} md={4.5} lg={5} sx={imageContainerStyles}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: "center", md: "start" }}
                pt={{ xs: 3, md: 0 }}
              >
                {trackmateData.map((data, index) => (
                  <Collapse in={activeStep === index} key={index}>
                    <StepperContentCard trackmateData={data} />
                  </Collapse>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default GetTrackmateStepper;
